import * as React from 'react';
import { useEffect, useState } from 'react';

import referrer from 'js/lib/referrer';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import * as actions from 'bundles/authentication/shared/actions';
import AuthenticationContentTracker from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContentTracker';
import CompleteSSO from 'bundles/authentication/shared/components/authentication-modal/CompleteSSO';
import ExpiredLink from 'bundles/authentication/shared/components/authentication-modal/ExpiredLink';
import ForgotPassword from 'bundles/authentication/shared/components/authentication-modal/ForgotPassword';
import LinkedAccount from 'bundles/authentication/shared/components/authentication-modal/LinkedAccount';
import Login from 'bundles/authentication/shared/components/authentication-modal/Login';
import MagicLink from 'bundles/authentication/shared/components/authentication-modal/MagicLink';
import OrganizationSSOLogin from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/OrganizationSSOLogin';
import OrganizationSSOSignup from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/OrganizationSSOSignup';
import RequestMagicLink from 'bundles/authentication/shared/components/authentication-modal/RequestMagicLink';
import SSO from 'bundles/authentication/shared/components/authentication-modal/SSO';
import Signup from 'bundles/authentication/shared/components/authentication-modal/Signup';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import { AuthModalTrackerContextProvider } from 'bundles/authentication/shared/components/authentication-modal/contexts/AuthenticationTracking';
import useForceSSOLoginQuery from 'bundles/authentication/shared/hooks/useForceSSOLoginQuery';
import useUserThirdPartyOrganizationsQuery from 'bundles/authentication/shared/hooks/useUserThirdPartyOrganizationsQuery';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication/shared/hooks/useUserThirdPartyOrganizationsQuery';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication/utils';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';

type Context = Record<string, any>;

export type Props = {
  defaultMode?:
    | typeof AUTHENTICATION_MODAL_MODES.SSO
    | typeof AUTHENTICATION_MODAL_MODES.LOGIN
    | typeof AUTHENTICATION_MODAL_MODES.SIGNUP
    | typeof AUTHENTICATION_MODAL_MODES.EXPIREDLINK;
  context: Context;
  redirectTo?: string;
  preventRegister?: boolean;
  magicLinkRedirect?: string;
  isStandaloneAuth?: boolean;
  programId?: string;
  forceSSOLogin?: boolean;
  switchFormsByQueryParam?: boolean;
};

const VIEWS: Record<AUTHENTICATION_MODAL_MODES_KEY, any> = {
  complete: CompleteSSO,
  error: Login,
  expired: ExpiredLink,
  forgot: ForgotPassword,
  linked: LinkedAccount,
  login: Login,
  magiclink: MagicLink,
  organizationlogin: OrganizationSSOLogin,
  organizationsignup: OrganizationSSOSignup,
  requestmagiclink: RequestMagicLink,
  signup: Signup,
  sso: SSO,
};

const styles = StyleSheet.create({
  container: {
    maxHeight: '100%',
    height: '100vh',
    overflow: 'auto',
    position: 'relative',
    '@media (min-width: 600px)': {
      height: 'auto',
    },
  },
  header: {
    '@media (min-width: 600px)': {
      display: 'none !important',
    },
  },
});

const AuthenticationContent = ({
  context: contextFromProps,
  defaultMode = AUTHENTICATION_MODAL_MODES.LOGIN,
  isStandaloneAuth,
  redirectTo,
  magicLinkRedirect,
  preventRegister,
  switchFormsByQueryParam = true,
  programId,
}: Props) => {
  const [context, setContext] = useState<Context>(contextFromProps ?? {});

  useEffect(() => {
    setContext(contextFromProps);
  }, [contextFromProps]);

  const [mode, setMode] = useState<AUTHENTICATION_MODAL_MODES_KEY>(defaultMode);
  const location = useLocation();
  const navigate = useNavigate();
  const { authProvider } = context;

  const { organizations } = useUserThirdPartyOrganizationsQuery(authProvider);

  const organizationId = organizations?.[0]?.id;
  const { forceSSOLogin } = useForceSSOLoginQuery(organizationId || '');

  const whitelistOrganizationProgramsForSSOLogin = EnterpriseExperiments.get(
    'whitelistOrganizationProgramsForSSOLogin'
  );

  // The structure of this configuration epic is a little strange, thus explaining the logic below
  /*
    "organization1Id": [],
    "organization2Id": [
      "program1Id",
      "program2Id"
     ],
   */
  const shouldShowCustomSSO = organizationId
    ? !whitelistOrganizationProgramsForSSOLogin[organizationId] ||
      whitelistOrganizationProgramsForSSOLogin[organizationId].includes(programId)
    : false;

  const ssoContext = {
    forceSSOLogin,
    organizations,
    shouldShowCustomSSO,
  };

  // add epic variable for XDPs CRO
  const onNavigate = (mode: AUTHENTICATION_MODAL_MODES_KEY, options: Context = {}) => {
    const areModesTheSame = location.pathname.includes(`/${mode}`);
    if (
      isStandaloneAuth &&
      !areModesTheSame &&
      (mode === AUTHENTICATION_MODAL_MODES.SIGNUP || mode === AUTHENTICATION_MODAL_MODES.LOGIN)
    ) {
      navigate(`/${mode}${location.search}`, { replace: true });
    } else {
      if (switchFormsByQueryParam) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('authMode', mode);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
      setContext({ ...contextFromProps, ...options });
      setMode(mode);
    }
  };

  const onLogin = (redirectURL?: string) => {
    actions.onLogin(redirectTo || redirectURL);
  };

  const onSignup = (redirectURL?: string, options?: { addShowTouAcceptParam?: boolean }) => {
    actions.onSignup(redirectTo || redirectURL, options);
  };

  const onSSOError = (error: SSOError) => {
    const { code } = error;

    switch (code) {
      case 'accountLinkedToSocialAccount':
        return onNavigate(AUTHENTICATION_MODAL_MODES.LINKED, { accountType: error.response?.existingAccountType });

      case 'existingCourseraAccount':
      case 'noEmailAvailable':
      default:
        return onNavigate(AUTHENTICATION_MODAL_MODES.COMPLETE, {
          mode: code,
          referrer: mode,
          service: error.service,
        });
    }
  };

  const onSSOSuccess = (isRegistration: boolean) => {
    if (isRegistration) {
      onSignup(undefined, { addShowTouAcceptParam: isTermsOfUseRolloutForUS() });
    } else {
      onLogin();
    }
  };

  const Content = (VIEWS[mode] || Login) as unknown as React.ComponentType<any>;

  return (
    <section {...css(styles.container)}>
      <AuthModalTrackerContextProvider
        value={
          mode === AUTHENTICATION_MODAL_MODES.LOGIN || mode === AUTHENTICATION_MODAL_MODES.SIGNUP
            ? { modalName: mode }
            : undefined
        }
      >
        <AuthenticationContentTracker />
        <Content
          redirect={redirectTo}
          magicLinkRedirect={magicLinkRedirect}
          navigate={onNavigate}
          onLogin={onLogin}
          onSignup={onSignup}
          onSSOError={onSSOError}
          onSSOSuccess={onSSOSuccess}
          preventRegister={preventRegister}
          {...ssoContext}
          {...context}
        />
      </AuthModalTrackerContextProvider>
    </section>
  );
};

export default AuthenticationContent;
